import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { EmailComposerProvider } from '@/components/email/EmailComposerProvider';
import { PhoneCallProvider } from '@/components/phone/PhoneCallProvider';
import { Toaster } from '@/components/ui/toaster';
import { ChatwootWidget } from '@/components/ChatwootWidget';
import { LoginPage } from '@/pages/auth/Login';
import { AuthCallback } from '@/pages/auth/Callback';
import AutoLogin from '@/pages/auth/AutoLogin';
import { EmailCallback } from '@/components/settings/EmailCallback';
import { OutlookCallback } from '@/components/settings/OutlookCallback';
import { OutlookConnected } from '@/components/settings/OutlookConnected';
import { CalendarCallback } from '@/components/settings/CalendarCallback';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { ContactsPage } from '@/pages/contacts';
import { ContactDetailsPage } from '@/pages/contacts/[id]';
import { FiltersPage } from '@/pages/filters';
import { FilterViewPage } from '@/pages/filters/[id]';
import { ListsPage } from '@/pages/lists';
import { GroupsPage } from '@/pages/groups';
import { CalendarPage } from '@/pages/calendar';
import { ActivitiesPage } from '@/pages/activities';
import AnalyticsPage from '@/pages/analytics';
import { EmailPage } from '@/pages/inbox';
import { CampaignDetailsPage } from '@/pages/inbox/campaigns/[id]';
import { CampaignSummaryPage } from '@/pages/inbox/campaigns/[id]/summary';
import { HermesUnavailable } from '@/pages/inbox/Unavailable';
import { isHermesUiEnabled } from '@/lib/feature-flags';
import { SettingsPage } from '@/pages/settings';
import { EnterpriseLayout } from '@/pages/enterprise/EnterpriseLayout';
import { EnterpriseEmailMonitoring } from '@/pages/enterprise/EnterpriseEmailMonitoring';
import { EnterpriseDashboard } from '@/pages/enterprise/EnterpriseDashboard';
import { EnterpriseSettings } from '@/pages/settings/enterprise/EnterpriseSettings';
import { EnterpriseActivate } from '@/pages/enterprise/EnterpriseActivate';
import { EnterpriseAdvancedEmailSettings } from '@/pages/enterprise/EnterpriseAdvancedEmailSettings';
import { EnterpriseBilling } from '@/pages/enterprise/EnterpriseBilling';
import { EnterpriseSsoGuide } from '@/pages/enterprise/EnterpriseSsoGuide';
import { VisualizationPage } from '@/pages/visualization';
import { DuplicatesPage } from '@/pages/DuplicatesPage';
import { HelpCenterPage } from '@/pages/help';
import { HelpCategoryPage } from '@/pages/help/[categorySlug]';
import { HelpArticlePage } from '@/pages/help/[categorySlug]/[articleSlug]';
import AcceptInvitation from '@/pages/AcceptInvitation';
import { EmailCheckPage } from '@/pages/tools/EmailCheck';
import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@/components/layout/ThemeProvider';
import { VersionUpdateBanner } from '@/components/layout/VersionUpdateBanner';
import { TourProvider } from '@/components/onboarding/TourProvider';
import { errorHandling } from '@/lib/error-handling';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on permanent errors
        if (!errorHandling.isRetryableError(error)) {
          return false;
        }
        // Retry up to 2 times for retryable errors
        return failureCount < 2;
      },
      retryDelay: errorHandling.getRetryDelay,
      
      // Cache configuration
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      
      // Refetch configuration
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      
      // Error handling
      throwOnError: false,
    },
    mutations: {
      // Global mutation error handling
      onError: (error) => {
        console.error('Mutation error:', error);
        // Could add global error reporting here
      },
      
      // Retry failed mutations
      retry: (failureCount, error) => {
        // Only retry network errors for mutations
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          return failureCount < 1; // Retry once
        }
        return false;
      },
      retryDelay: 1000, // 1 second delay for mutation retries
    },
  },
});

function App() {
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();

  // Only keep search term in URL when on contacts page
  // But this should NOT affect the contacts filter directly
  useEffect(() => {
    if (location.pathname.startsWith('/contacts')) {
      // Keep search term if it's in the URL query params
      const searchParams = new URLSearchParams(location.search);
      const searchFromUrl = searchParams.get('search');
      
      if (!searchFromUrl && searchTerm) {
        setSearchTerm("");
      } else if (searchFromUrl) {
        setSearchTerm(searchFromUrl);
      }
    }
  }, [location.pathname, location.search, searchTerm]);

  // Update URL with search term when on contacts page
  useEffect(() => {
    if (location.pathname === '/contacts' && searchTerm) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('search', searchTerm);
      
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [searchTerm, location.pathname, location.search]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="theme">
        <div className="w-full min-h-screen flex flex-col bg-background text-foreground">
          <AuthProvider>
            <WorkspaceProvider>
              <TourProvider>
              <EmailComposerProvider>
              <PhoneCallProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auto-login" element={<AutoLogin />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  {/* Public (no login): "Is my email Google or Microsoft?" */}
                  <Route path="/tools/email-check" element={<EmailCheckPage />} />
                  <Route path="/settings/email/callback" element={<EmailCallback />} />
                  <Route path="/settings/outlook/callback" element={<OutlookCallback />} />
                  <Route path="/settings/outlook/connected" element={<OutlookConnected />} />
                  <Route path="/settings/calendar/callback" element={<CalendarCallback />} />
                  <Route path="/" element={<DashboardLayout searchValue={searchTerm} onSearch={setSearchTerm} />}>
                    <Route path="contacts" element={<ContactsPage />} />
                    <Route path="contacts/:id" element={<ContactDetailsPage />} />
                    <Route path="filters" element={<FiltersPage />} />
                    <Route path="filters/:id" element={<FilterViewPage />} />
                    <Route path="lists" element={<ListsPage />} />
                    <Route path="groups" element={<GroupsPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="activities" element={<ActivitiesPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    {/* Hermes UI feature flag (Phase 3 fix 3.D) — when disabled,
                        all /inbox routes resolve to the unavailable placeholder. */}
                    <Route
                      path="inbox"
                      element={isHermesUiEnabled() ? <EmailPage /> : <HermesUnavailable />}
                    />
                    <Route
                      path="inbox/campaigns/:id/summary"
                      element={isHermesUiEnabled() ? <CampaignSummaryPage /> : <HermesUnavailable />}
                    />
                    <Route
                      path="inbox/campaigns/:id"
                      element={isHermesUiEnabled() ? <CampaignDetailsPage /> : <HermesUnavailable />}
                    />
                    {/* Legacy Settings→Enterprise path now redirects into the shell.
                        Static `settings/enterprise` outranks the `settings/*` splat
                        in RR v6 ranking, so this matches first. */}
                    <Route path="settings/enterprise" element={<Navigate to="/enterprise/members" replace />} />
                    <Route path="settings/*" element={<SettingsPage />} />
                    {/* Enterprise first-login activation (#615). A manually-added
                        member who clicks the first-login link is a REGULAR member,
                        NOT an admin — so this route is OUTSIDE the admin-gated
                        EnterpriseLayout. It's authenticated (DashboardLayout gates
                        on `user`) and reachable by any signed-in user. The static
                        `enterprise/activate` segment outranks the `enterprise`
                        layout's dynamic children in RR v6, so it matches here. */}
                    <Route path="enterprise/activate" element={<EnterpriseActivate />} />
                    {/* M4 (#569): unified enterprise shell — nested layout w/ sky sub-nav.
                        Children compose the M2/M3 pages; URLs /enterprise/email +
                        /enterprise/dashboard are PRESERVED (deep-links depend on them). */}
                    <Route path="enterprise" element={<EnterpriseLayout />}>
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<EnterpriseDashboard />} />
                      <Route path="email" element={<EnterpriseEmailMonitoring />} />
                      <Route path="members" element={<EnterpriseSettings />} />
                      <Route path="sso" element={<EnterpriseSsoGuide />} />
                      <Route path="advanced" element={<EnterpriseAdvancedEmailSettings />} />
                      <Route path="billing" element={<EnterpriseBilling />} />
                    </Route>
                    <Route path="visualization" element={<VisualizationPage />} />
                    <Route path="duplicates" element={<DuplicatesPage />} />
                    <Route path="help" element={<HelpCenterPage />} />
                    <Route path="help/:categorySlug" element={<HelpCategoryPage />} />
                    <Route path="help/:categorySlug/:articleSlug" element={<HelpArticlePage />} />
                    <Route index element={<Navigate to="/calendar" replace />} />
                  </Route>
                </Routes>
                <Toaster />
                <ChatwootWidget />
                <VersionUpdateBanner />
              </PhoneCallProvider>
              </EmailComposerProvider>
              </TourProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </div>
        {import.meta.env.MODE === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
